// Thermal Dimension View - 1D → 2D → 3D visualization for thermal properties with 4 Experimental Conditions
import { useEffect, useRef, useState } from 'preact/hooks'
import { basis, domain } from '../../../state/globalSignals.js'
import * as echarts from 'echarts'
import { COMPARE_COLORS } from '../../compare/views/ComparePlotsView.jsx'
import { formatValue } from '../../../utils/formatUtils.js'
// Note: Plotly loaded globally via CDN in index.html

// Color scheme for 4 experimental conditions (matching Energy/Power sections)
const CONDITION_COLORS = {
  c10: '#ea5b0c',      // Orange - C/10
  dc_25: '#0077b6',    // Blue - Standard
  cont: '#d62828',     // Red - Continuous
  peak: '#48cae4'      // Cyan - Peak
}

// Helper: compute thermal spec point values from DCC arrays
// This is needed because dataService might not have electrical data when thermal is normalized
function computeThermalSpecPoints(cellData, categoryName) {
  const elec = cellData?.electrical || {}
  const therm = cellData?.thermal || {}
  
  // Get DCC arrays from electrical (these are normalized: T in °C)
  const P_DCC = elec.P_DCC || []
  const T_amb_DCC = elec.T_amb_DCC || []
  
  // Get thermal DCC arrays (these are normalized: T in °C, W in Wh, P in W)
  const P_loss_DCC = therm.P_loss_dcc_W || []
  const W_loss_DCC = therm.E_loss_dcc_Wh || []
  const T_max_DCC = therm.T_max_dcc_C || []
  const eta_DCC = elec.eta_DCC || []
  
  // Reference power values for 4 conditions
  const P_C10 = elec.P_dc_C10 || 0
  const P_dc_25 = elec.P_dc_25_W || 0
  const P_cont = elec.P_dc_cont || 0
  const P_peak = elec.P_dc_peak || 0
  
  // Find closest index in DCC arrays at T_amb ≈ 25°C
  const findClosestAt25C = (targetP) => {
    let closestIdx = -1
    let minDist = Infinity
    
    for (let i = 0; i < P_DCC.length; i++) {
      // T_amb_DCC is already in °C (converted by dataService)
      const T_amb = T_amb_DCC[i]
      if (Math.abs(T_amb - 25) > 1) continue // Only ~25°C points
      
      const dist = Math.abs(P_DCC[i] - targetP)
      if (dist < minDist) {
        minDist = dist
        closestIdx = i
      }
    }
    
    return closestIdx
  }
  
  const idx_c10 = findClosestAt25C(P_C10)
  const idx_cont = findClosestAt25C(P_cont)
  const idx_peak = findClosestAt25C(P_peak)
  
  console.log('[ThermalDimensionView] computeThermalSpecPoints:', {
    P_refs: { P_C10, P_dc_25, P_cont, P_peak },
    indices: { idx_c10, idx_cont, idx_peak },
    arrayLengths: { P_DCC: P_DCC.length, T_amb_DCC: T_amb_DCC.length, P_loss_DCC: P_loss_DCC.length }
  })

  // DEBUG: Show what values we found
  if (idx_c10 >= 0) console.log(`  idx_c10=${idx_c10}: P_DCC=${P_DCC[idx_c10]?.toFixed(2)}, T_amb=${T_amb_DCC[idx_c10]?.toFixed(1)}°C`)
  if (idx_cont >= 0) console.log(`  idx_cont=${idx_cont}: P_DCC=${P_DCC[idx_cont]?.toFixed(2)}, T_amb=${T_amb_DCC[idx_cont]?.toFixed(1)}°C`)
  if (idx_peak >= 0) {
    console.log(`  idx_peak=${idx_peak}: P_DCC=${P_DCC[idx_peak]?.toFixed(2)}, T_amb=${T_amb_DCC[idx_peak]?.toFixed(1)}°C`)
    // Show all available values in DCC arrays at this index
    const dcc_info = {
      P_loss_DCC: P_loss_DCC[idx_peak]?.toFixed(2),
      W_loss_DCC: W_loss_DCC[idx_peak]?.toFixed(2),
      T_max_DCC: T_max_DCC[idx_peak]?.toFixed(1),
      eta_DCC: eta_DCC[idx_peak]?.toFixed(4)
    }
    console.log(`    DCC values at idx_peak: ${JSON.stringify(dcc_info)}`)
  }
  
  if (categoryName === 'heat-power') {
    const peak_val = idx_peak >= 0 ? P_loss_DCC[idx_peak] : 0
    console.log(`  heat-power: Peak Z-value from DCC = ${peak_val?.toFixed(2)} W`)
  } else if (categoryName === 'heat-energy') {
    const peak_val = idx_peak >= 0 ? W_loss_DCC[idx_peak] : 0
    console.log(`  heat-energy: Peak Z-value from DCC = ${peak_val?.toFixed(2)} Wh`)
  }
  
  // Get values based on category
  if (categoryName === 'heat-power') {
    return {
      c10: idx_c10 >= 0 ? P_loss_DCC[idx_c10] : 0,
      dc_25: therm.P_loss_dc_25 || 0, // Use scalar
      cont: idx_cont >= 0 ? P_loss_DCC[idx_cont] : 0,
      peak: idx_peak >= 0 ? P_loss_DCC[idx_peak] : 0
    }
  } else if (categoryName === 'heat-energy') {
    return {
      c10: idx_c10 >= 0 ? W_loss_DCC[idx_c10] : 0,
      dc_25: therm.W_loss_dc_25 || 0, // Use scalar (already in Wh)
      cont: idx_cont >= 0 ? W_loss_DCC[idx_cont] : 0,
      peak: idx_peak >= 0 ? W_loss_DCC[idx_peak] : 0
    }
  } else if (categoryName === 'temperature') {
    return {
      c10: idx_c10 >= 0 ? T_max_DCC[idx_c10] : 0,
      dc_25: therm.T_max_dc_25 || 0, // Use scalar (already in °C)
      cont: idx_cont >= 0 ? T_max_DCC[idx_cont] : 0,
      peak: idx_peak >= 0 ? T_max_DCC[idx_peak] : 0
    }
  } else if (categoryName === 'efficiency') {
    return {
      c10: idx_c10 >= 0 ? (eta_DCC[idx_c10] * 100) : 0,
      dc_25: (elec.eta_dc_pct || 0), // Already in %
      cont: idx_cont >= 0 ? (eta_DCC[idx_cont] * 100) : 0,
      peak: idx_peak >= 0 ? (eta_DCC[idx_peak] * 100) : 0
    }
  }
  
  return { c10: 0, dc_25: 0, cont: 0, peak: 0 }
}

export default function ThermalDimensionView({ 
  cellData,          // Single cell mode
  cellDataArray,     // Compare mode: array of cells
  isCompareMode = false,
  categoryName, 
  dimension 
}) {
  const plot2dRef1 = useRef(null)
  const plot2dRef2 = useRef(null)
  const plot3dRef = useRef(null)
  const chartInstance1 = useRef(null)
  const chartInstance2 = useRef(null)
  const [data1D, setData1D] = useState(null) // Store 1D data in state
  // Note: Plotly doesn't need chart instance ref, manages DOM directly

  // Calculate 1D data whenever relevant dependencies change
  useEffect(() => {
    console.log('[ThermalDimensionView] 1D useEffect:', { dimension, categoryName, hasCellData: !!cellData })
    if (dimension === '1d') {
      const newData = get1DData()
      console.log('[ThermalDimensionView] Setting 1D data:', newData)
      setData1D(newData)
    }
  }, [dimension, categoryName, cellData, basis.value])

  useEffect(() => {
    if (dimension === '1d') {
      // Cleanup 2D/3D plots
      if (chartInstance1.current) {
        chartInstance1.current.dispose()
        chartInstance1.current = null
      }
      if (chartInstance2.current) {
        chartInstance2.current.dispose()
        chartInstance2.current = null
      }
      // Plotly handles its own cleanup
      if (plot3dRef.current && window.Plotly) {
        window.Plotly.purge(plot3dRef.current)
      }
      // Note: 1D data is calculated in separate useEffect above
    } else if (dimension === '2d') {
      // Cleanup 3D plot
      if (plot3dRef.current && window.Plotly) {
        window.Plotly.purge(plot3dRef.current)
      }
      // Render 2D plots on next frame
      requestAnimationFrame(() => {
        const data = getDataForCategory(cellData, categoryName, basis.value)
        if (data) render2DPlots(data, categoryName)
      })
    } else if (dimension === '3d') {
      // Cleanup 2D plots
      if (chartInstance1.current) {
        chartInstance1.current.dispose()
        chartInstance1.current = null
      }
      if (chartInstance2.current) {
        chartInstance2.current.dispose()
        chartInstance2.current = null
      }
      // Render 3D plot
      requestAnimationFrame(() => {
        const data = getDataForCategory(cellData, categoryName, basis.value)
        if (data) render3DPlot(data, categoryName)
      })
    }

    return () => {
      if (chartInstance1.current) {
        chartInstance1.current.dispose()
        chartInstance1.current = null
      }
      if (chartInstance2.current) {
        chartInstance2.current.dispose()
        chartInstance2.current = null
      }
      if (plot3dRef.current && window.Plotly) {
        window.Plotly.purge(plot3dRef.current)
      }
    }
  }, [cellData, categoryName, dimension, basis.value, domain.value])

  // Get 1D data for display - 4 values per category
  function get1DData() {
    if (!cellData?.thermal) return null

    const basisValue = basis.value
    const m_kg = cellData?.mechanical?.mass_g ? cellData.mechanical.mass_g / 1000 : null
    const vol_L = cellData?.mechanical?.vol_cell_cm3 ? cellData.mechanical.vol_cell_cm3 / 1000 : null
    
    // Compute spec point values from DCC arrays (fallback if dataService values are 0)
    const computed = computeThermalSpecPoints(cellData, categoryName)
    const therm = cellData.thermal

    if (categoryName === 'heat-power') {
      // Power loss [W] for 4 conditions - prefer computed, fallback to dataService
      let p_c10 = computed.c10 || therm?.P_loss_c10 || 0;
      let p_dc_25 = computed.dc_25 || therm?.P_loss_dc_25 || 0;
      let p_cont = computed.cont || therm?.P_loss_cont || 0;
      let p_peak = computed.peak || therm?.P_loss_peak || 0;
      let unit = 'W';
      
      if (basisValue === 'grav' && m_kg) {
        p_c10 /= m_kg;
        p_dc_25 /= m_kg;
        p_cont /= m_kg;
        p_peak /= m_kg;
        unit = 'W/kg';
      } else if (basisValue === 'vol' && vol_L) {
        p_c10 /= vol_L;
        p_dc_25 /= vol_L;
        p_cont /= vol_L;
        p_peak /= vol_L;
        unit = 'W/L';
      }
      
      return {
        c10: { value: p_c10, unit, label: 'C/10', color: CONDITION_COLORS.c10 },
        dc_25: { value: p_dc_25, unit, label: 'Standard', color: CONDITION_COLORS.dc_25 },
        cont: { value: p_cont, unit, label: 'Continuous', color: CONDITION_COLORS.cont },
        peak: { value: p_peak, unit, label: 'Peak', color: CONDITION_COLORS.peak }
      };
      
    } else if (categoryName === 'heat-energy') {
      // Energy loss [Wh] for 4 conditions - prefer computed, fallback to dataService
      let w_c10 = computed.c10 || therm?.W_loss_c10 || 0;
      let w_dc_25 = computed.dc_25 || therm?.W_loss_dc_25 || 0;
      let w_cont = computed.cont || therm?.W_loss_cont || 0;
      let w_peak = computed.peak || therm?.W_loss_peak || 0;
      let unit = 'Wh';
      
      if (basisValue === 'grav' && m_kg) {
        w_c10 /= m_kg;
        w_dc_25 /= m_kg;
        w_cont /= m_kg;
        w_peak /= m_kg;
        unit = 'Wh/kg';
      } else if (basisValue === 'vol' && vol_L) {
        w_c10 /= vol_L;
        w_dc_25 /= vol_L;
        w_cont /= vol_L;
        w_peak /= vol_L;
        unit = 'Wh/L';
      }
      
      return {
        c10: { value: w_c10, unit, label: 'C/10', color: CONDITION_COLORS.c10 },
        dc_25: { value: w_dc_25, unit, label: 'Standard', color: CONDITION_COLORS.dc_25 },
        cont: { value: w_cont, unit, label: 'Continuous', color: CONDITION_COLORS.cont },
        peak: { value: w_peak, unit, label: 'Peak', color: CONDITION_COLORS.peak }
      };
      
    } else if (categoryName === 'temperature') {
      // Max temperature [°C] for 4 conditions - prefer computed, fallback to dataService
      const t_c10 = computed.c10 || therm?.T_max_c10 || 0;
      const t_dc_25 = computed.dc_25 || therm?.T_max_dc_25 || 0;
      const t_cont = computed.cont || therm?.T_max_cont || 0;
      const t_peak = computed.peak || therm?.T_max_peak || 0;
      
      return {
        c10: { value: t_c10, unit: '°C', label: 'C/10', color: CONDITION_COLORS.c10 },
        dc_25: { value: t_dc_25, unit: '°C', label: 'Standard', color: CONDITION_COLORS.dc_25 },
        cont: { value: t_cont, unit: '°C', label: 'Continuous', color: CONDITION_COLORS.cont },
        peak: { value: t_peak, unit: '°C', label: 'Peak', color: CONDITION_COLORS.peak }
      };
      
    } else if (categoryName === 'efficiency') {
      // Efficiency [%] for 4 conditions - prefer computed, fallback to dataService
      const eta_c10 = computed.c10 || therm?.eta_c10 || 0;
      const eta_dc_25 = computed.dc_25 || therm?.eta_dc_25 || 0;
      const eta_cont = computed.cont || therm?.eta_cont || 0;
      const eta_peak = computed.peak || therm?.eta_peak || 0;
      
      return {
        c10: { value: eta_c10, unit: '%', label: 'C/10', color: CONDITION_COLORS.c10 },
        dc_25: { value: eta_dc_25, unit: '%', label: 'Standard', color: CONDITION_COLORS.dc_25 },
        cont: { value: eta_cont, unit: '%', label: 'Continuous', color: CONDITION_COLORS.cont },
        peak: { value: eta_peak, unit: '%', label: 'Peak', color: CONDITION_COLORS.peak }
      };
    }

    return null;
  }

  function getDataForCategory(cellData, category, basisValue) {
    if (!cellData) return null

    const m_kg = cellData?.mechanical?.mass_g ? cellData.mechanical.mass_g / 1000 : null
    const vol_L = cellData?.mechanical?.vol_cell_cm3 ? cellData.mechanical.vol_cell_cm3 / 1000 : null
    // Map global domain signal to power/current for thermal plots
    // 'energy-power' → use Power (P_DCC), 'capacity-current' → use Current (I_DCC)
    const domainValue = domain.value === 'energy-power' ? 'power' : 'current'

    let X, Y, Z, X0, Y0, Z0
    let xLabel, yLabel, zLabel, xUnit, yUnit, zUnit

    if (category === 'heat-power') {
      // Heat Power Loss [W]
      const P_DCC = cellData?.electrical?.P_DCC || []
      const I_DCC = cellData?.electrical?.I_DCC || []
      const T_amb_DCC = cellData?.electrical?.T_amb_DCC || []
      const P_loss_DCC = cellData?.thermal?.P_loss_dcc_W || []
      
      X = domainValue === 'power' ? [...P_DCC] : [...I_DCC]
      Y = [...T_amb_DCC]
      Z = [...P_loss_DCC]
      
      // X0: Use Power or Current for Standard condition based on domain
      // X0: Use Power or Current for Standard condition based on domain
      X0 = domainValue === 'power' 
        ? (cellData?.electrical?.P_dc_25 || 0)
        : (cellData?.electrical?.I_dc_25 || cellData?.electrical?.I_dc_25 || 0)
      Y0 = 25
      Z0 = cellData?.thermal?.P_loss_dc_25 || 0
      
      xLabel = domainValue === 'power' ? 'Power' : 'Current'
      yLabel = 'Ambient Temperature'
      zLabel = 'Heat Power'
      xUnit = domainValue === 'power' ? 'W' : 'A'
      yUnit = '°C'
      zUnit = 'W'
      
      // Apply basis scaling (both Power and Current scale with mass/volume)
      if (basisValue === 'grav' && m_kg) {
        X = X.map(x => x / m_kg)
        Z = Z.map(z => z / m_kg)
        X0 = X0 / m_kg
        Z0 = Z0 / m_kg
        xUnit = domainValue === 'power' ? 'W/kg' : 'A/kg'
        zUnit = 'W/kg'
      } else if (basisValue === 'vol' && vol_L) {
        X = X.map(x => x / vol_L)
        Z = Z.map(z => z / vol_L)
        X0 = X0 / vol_L
        Z0 = Z0 / vol_L
        xUnit = domainValue === 'power' ? 'W/L' : 'A/L'
        zUnit = 'W/L'
      }
      
      // Fallback for X0 if 0 (should not happen with valid data)
      if (!X0 || X0 === 0) {
        const sortedX = [...X].sort((a, b) => a - b)
        X0 = sortedX[Math.floor(sortedX.length / 2)] || 1
      }
      // Note: Z0 is already correctly set from P_loss_dc_25 and scaled above
      
    } else if (category === 'heat-energy') {
      // Heat Energy Loss [Wh]
      const P_DCC = cellData?.electrical?.P_DCC || []
      const I_DCC = cellData?.electrical?.I_DCC || []
      const T_amb_DCC = cellData?.electrical?.T_amb_DCC || []
      const W_loss_DCC = cellData?.thermal?.E_loss_dcc_Wh || []
      
      X = domainValue === 'power' ? [...P_DCC] : [...I_DCC]
      Y = [...T_amb_DCC]
      Z = [...W_loss_DCC]
      
      // X0: Use Power or Current for Standard condition based on domain
      // X0: Use Power or Current for Standard condition based on domain
      X0 = domainValue === 'power' 
        ? (cellData?.electrical?.P_dc_25 || 0)
        : (cellData?.electrical?.I_dc_25 || cellData?.electrical?.I_dc_25 || 0)
      Y0 = 25
      Z0 = cellData?.thermal?.W_loss_dc_25 || 0
      
      xLabel = domainValue === 'power' ? 'Power' : 'Current'
      yLabel = 'Ambient Temperature'
      zLabel = 'Heat Energy'
      xUnit = domainValue === 'power' ? 'W' : 'A'
      yUnit = '°C'
      zUnit = 'Wh'
      
      // Apply basis scaling (both Power and Current scale with mass/volume)
      if (basisValue === 'grav' && m_kg) {
        X = X.map(x => x / m_kg)
        Z = Z.map(z => z / m_kg)
        X0 = X0 / m_kg
        Z0 = Z0 / m_kg
        xUnit = domainValue === 'power' ? 'W/kg' : 'A/kg'
        zUnit = 'Wh/kg'
      } else if (basisValue === 'vol' && vol_L) {
        X = X.map(x => x / vol_L)
        Z = Z.map(z => z / vol_L)
        X0 = X0 / vol_L
        Z0 = Z0 / vol_L
        xUnit = domainValue === 'power' ? 'W/L' : 'A/L'
        zUnit = 'Wh/L'
      }
      
      // Fallback for X0 if 0 (should not happen with valid data)
      if (!X0 || X0 === 0) {
        const sortedX = [...X].sort((a, b) => a - b)
        X0 = sortedX[Math.floor(sortedX.length / 2)] || 1
      }
      // Note: Z0 is already correctly set from W_loss_dc_25 and scaled above
      
    } else if (category === 'temperature') {
      // Use already normalized data from dataService (K → °C conversion already done)
      const T_max_DCC = cellData?.thermal?.T_max_dcc_C || []
      const P_DCC = cellData?.electrical?.P_DCC || []
      const I_DCC = cellData?.electrical?.I_DCC || []
      const T_amb_DCC = cellData?.electrical?.T_amb_DCC || [] // Already in °C from dataService
      
      X = domainValue === 'power' ? [...P_DCC] : [...I_DCC]
      Y = [...T_amb_DCC]
      Z = [...T_max_DCC]
      
      // X0: Use Power or Current for Standard condition based on domain
      // X0: Use Power or Current for Standard condition based on domain
      X0 = domainValue === 'power' 
        ? (cellData?.electrical?.P_dc_25 || 0)
        : (cellData?.electrical?.I_dc_25 || cellData?.electrical?.I_dc_25 || 0)
      Y0 = 25
      Z0 = (cellData?.thermal?.delta_T_dc_25 || 0) + 25
      
      xLabel = domainValue === 'power' ? 'Power' : 'Current'
      yLabel = 'Ambient Temperature'
      zLabel = 'Max Surface Temperature'
      xUnit = domainValue === 'power' ? 'W' : 'A'
      yUnit = '°C'
      zUnit = '°C'
      
      // Apply basis scaling to X (both Power and Current scale with mass/volume)
      if (basisValue === 'grav' && m_kg) {
        X = X.map(x => x / m_kg)
        X0 = X0 / m_kg
        xUnit = domainValue === 'power' ? 'W/kg' : 'A/kg'
      } else if (basisValue === 'vol' && vol_L) {
        X = X.map(x => x / vol_L)
        X0 = X0 / vol_L
        xUnit = domainValue === 'power' ? 'W/L' : 'A/L'
      }
      
      // Fallback for X0 if 0 (should not happen with valid data)
      if (!X0 || X0 === 0) {
        const sortedX = [...X].sort((a, b) => a - b)
        X0 = sortedX[Math.floor(sortedX.length / 2)] || 1
      }
      // Note: Z0 is already correctly set from delta_T_dc_25 + 25 (no scaling needed for temperature)
      
    } else if (category === 'efficiency') {
      const eta_DCC = cellData?.electrical?.eta_DCC || []
      const P_DCC = cellData?.electrical?.P_DCC || []
      const I_DCC = cellData?.electrical?.I_DCC || []
      const T_amb_DCC = cellData?.electrical?.T_amb_DCC || []
      
      X = domainValue === 'power' ? [...P_DCC] : [...I_DCC]
      Y = [...T_amb_DCC]
      Z = eta_DCC.map(eta => eta * 100) // Convert to percentage
      
      // X0: Use Power or Current for Standard condition based on domain
      // X0: Use Power or Current for Standard condition based on domain
      X0 = domainValue === 'power' 
        ? (cellData?.electrical?.P_dc_25 || 0)
        : (cellData?.electrical?.I_dc_25 || cellData?.electrical?.I_dc_25 || 0)
      Y0 = 25
      Z0 = (cellData?.electrical?.eta_dc_pct || 0)
      
      xLabel = domainValue === 'power' ? 'Power' : 'Current'
      yLabel = 'Ambient Temperature'
      zLabel = 'Efficiency'
      xUnit = domainValue === 'power' ? 'W' : 'A'
      yUnit = '°C'
      zUnit = '%'
      
      // Apply basis scaling to X (both Power and Current scale with mass/volume)
      if (basisValue === 'grav' && m_kg) {
        X = X.map(x => x / m_kg)
        X0 = X0 / m_kg
        xUnit = domainValue === 'power' ? 'W/kg' : 'A/kg'
      } else if (basisValue === 'vol' && vol_L) {
        X = X.map(x => x / vol_L)
        X0 = X0 / vol_L
        xUnit = domainValue === 'power' ? 'W/L' : 'A/L'
      }
      
      // Fallback for X0 if 0 (should not happen with valid data)
      if (!X0 || X0 === 0) {
        const sortedX = [...X].sort((a, b) => a - b)
        X0 = sortedX[Math.floor(sortedX.length / 2)] || 1
      }
      // Note: Z0 is already correctly set from eta_dc_pct (no scaling needed for efficiency %)
    }

    // === Add 4 Spec Point X values (Power or Current) for Chart 2 slices ===
    let P_C10, P_dc_25, P_cont, P_peak, I_C10, I_dc_25, I_cont, I_peak
    
    if (basisValue === 'grav' && m_kg) {
      P_C10 = (cellData?.electrical?.P_dc_C10 || 0) / m_kg
      P_dc_25 = (cellData?.electrical?.P_dc_25 || 0) / m_kg
      P_cont = (cellData?.electrical?.P_dc_cont || 0) / m_kg
      P_peak = (cellData?.electrical?.P_dc_peak || 0) / m_kg
      I_C10 = (cellData?.electrical?.I_dc_C10 || 0) / m_kg
      I_dc_25 = (cellData?.electrical?.I_dc_25 || 0) / m_kg
      I_cont = (cellData?.electrical?.I_dc_cont || 0) / m_kg
      I_peak = (cellData?.electrical?.I_dc_peak || 0) / m_kg
    } else if (basisValue === 'vol' && vol_L) {
      P_C10 = (cellData?.electrical?.P_dc_C10 || 0) / vol_L
      P_dc_25 = (cellData?.electrical?.P_dc_25 || 0) / vol_L
      P_cont = (cellData?.electrical?.P_dc_cont || 0) / vol_L
      P_peak = (cellData?.electrical?.P_dc_peak || 0) / vol_L
      I_C10 = (cellData?.electrical?.I_dc_C10 || 0) / vol_L
      I_dc_25 = (cellData?.electrical?.I_dc_25 || 0) / vol_L
      I_cont = (cellData?.electrical?.I_dc_cont || 0) / vol_L
      I_peak = (cellData?.electrical?.I_dc_peak || 0) / vol_L
    } else {
      P_C10 = cellData?.electrical?.P_dc_C10 || 0
      P_dc_25 = cellData?.electrical?.P_dc_25 || 0
      P_cont = cellData?.electrical?.P_dc_cont || 0
      P_peak = cellData?.electrical?.P_dc_peak || 0
      I_C10 = cellData?.electrical?.I_dc_C10 || 0
      I_dc_25 = cellData?.electrical?.I_dc_25 || 0
      I_cont = cellData?.electrical?.I_dc_cont || 0
      I_peak = cellData?.electrical?.I_dc_peak || 0
    }

    // === Add 4 Spec Point Z values (thermal values at 25°C) ===
    // Helper to scale Z values based on category and basis
    const scaleZ = (value) => {
      if (category === 'temperature' || category === 'efficiency') return value
      if (basisValue === 'grav' && m_kg) return value / m_kg
      if (basisValue === 'vol' && vol_L) return value / vol_L
      return value
    }

    let Z_C10, Z_dc_25, Z_cont, Z_peak
    if (category === 'heat-power') {
      Z_C10 = scaleZ(cellData?.thermal?.P_loss_c10 || 0)
      Z_dc_25 = scaleZ(cellData?.thermal?.P_loss_dc_25 || 0)
      Z_cont = scaleZ(cellData?.thermal?.P_loss_cont || 0)
      Z_peak = scaleZ(cellData?.thermal?.P_loss_peak || 0)
    } else if (category === 'heat-energy') {
      Z_C10 = scaleZ(cellData?.thermal?.W_loss_c10 || 0)
      Z_dc_25 = scaleZ(cellData?.thermal?.W_loss_dc_25 || 0)
      Z_cont = scaleZ(cellData?.thermal?.W_loss_cont || 0)
      Z_peak = scaleZ(cellData?.thermal?.W_loss_peak || 0)
    } else if (category === 'temperature') {
      Z_C10 = cellData?.thermal?.T_max_c10 || 0
      Z_dc_25 = cellData?.thermal?.T_max_dc_25 || 0
      Z_cont = cellData?.thermal?.T_max_cont || 0
      Z_peak = cellData?.thermal?.T_max_peak || 0
    } else if (category === 'efficiency') {
      // eta values from thermal (already in % from dataService)
      Z_C10 = cellData?.thermal?.eta_c10 || 0
      Z_dc_25 = cellData?.thermal?.eta_dc_25 || 0
      Z_cont = cellData?.thermal?.eta_cont || 0
      Z_peak = cellData?.thermal?.eta_peak || 0
    }

    return { 
      X, Y, Z, X0, Y0, Z0, 
      xLabel, yLabel, zLabel, xUnit, yUnit, zUnit,
      // 4 Spec Point X values (Power or Current)
      P_C10, P_dc_25, P_cont, P_peak,
      I_C10, I_dc_25, I_cont, I_peak,
      // 4 Spec Point Z values (thermal values at 25°C)
      Z_C10, Z_dc_25, Z_cont, Z_peak
    }
  }

  function render2DPlots(data, categoryName) {
    if (!plot2dRef1.current || !plot2dRef2.current) return
    if (!data || !data.X || !data.Y || !data.Z) {
      console.error(`❌ THERMAL RENDER2D: Missing data for ${categoryName}:`, {
        hasData: !!data,
        hasX: !!data?.X,
        hasY: !!data?.Y,
        hasZ: !!data?.Z,
        xLen: data?.X?.length || 0,
        yLen: data?.Y?.length || 0,
        zLen: data?.Z?.length || 0
      })
      return
    }

    console.log(`🔍 === THERMAL RENDERING 2D ${categoryName} ===`)
    console.log('📊 RAW ARRAY DATA:')
    console.log('  X length:', data.X.length, '| First 10:', data.X.slice(0, 10).map(v => v.toFixed(2)))
    console.log('  Y length:', data.Y.length, '| First 10:', data.Y.slice(0, 10).map(v => v.toFixed(2)))
    console.log('  Z length:', data.Z.length, '| First 10:', data.Z.slice(0, 10).map(v => v.toFixed(2)))
    console.log('  Ranges - X:', Math.min(...data.X).toFixed(2), 'to', Math.max(...data.X).toFixed(2))
    console.log('  Ranges - Y:', Math.min(...data.Y).toFixed(2), 'to', Math.max(...data.Y).toFixed(2))
    console.log('  Ranges - Z:', Math.min(...data.Z).toFixed(2), 'to', Math.max(...data.Z).toFixed(2))

    // Color schemes for spec points (Row 1 in legend) and temperature curves (Row 2 in legend)
    // SAME AS ELECTRICAL DimensionView.jsx
    const specPointColors = {
      'C/10': '#ea5b0c',       // Batemo Orange
      'Standard': '#0077b6',   // Blue
      'Continuous': '#d62828', // Red
      'Peak': '#48cae4'        // Cyan
    }
    // Temperature curve colors (from cold to hot) - SAME AS ELECTRICAL
    const TEMP_COLORS = [
      '#0077b6',  // Cold blue (-25°C)
      '#00b4d8',  // Light blue (0°C)
      '#ea5b0c',  // Orange (25°C) - reference
      '#d62828',  // Red (40°C)
      '#9d0208',  // Dark red (hot)
      '#7b2cbf',  // Purple (extra)
      '#2d6a4f',  // Green (extra)
    ]

    // === Prepare helpers for basis scaling ===
    const m_kg = cellData?.mechanical?.mass_g ? cellData.mechanical.mass_g / 1000 : null
    const vol_L = cellData?.mechanical?.vol_cell_cm3 ? cellData.mechanical.vol_cell_cm3 / 1000 : null
    const basisValue = basis.value

    const scaleX = (value) => {
      if (basisValue === 'grav' && m_kg) return value / m_kg
      if (basisValue === 'vol' && vol_L) return value / vol_L
      return value
    }

    const scaleZ = (value) => {
      // Temperature and efficiency have no basis scaling
      if (categoryName === 'temperature' || categoryName === 'efficiency') return value
      // Heat-power and heat-energy have basis scaling
      if (basisValue === 'grav' && m_kg) return value / m_kg
      if (basisValue === 'vol' && vol_L) return value / vol_L
      return value
    }

    // heat-power and heat-energy can have negative values, temperature and efficiency should be >= 0
    const allowNegativeZ = categoryName === 'heat-power' || categoryName === 'heat-energy'

    // === CHART 1: Z vs X, one curve per unique temperature ===
    // Extract unique temperature values from Y array
    const uniqueTemps = [...new Set(data.Y.map(t => Math.round(t * 10) / 10))].sort((a, b) => a - b)
    console.log(`🌡️ Unique temperatures found for Chart 1 (${categoryName}):`, uniqueTemps)

    // Build one slice per temperature
    const tempSlices = uniqueTemps.map((temp, idx) => {
      const points = []
      for (let i = 0; i < data.X.length; i++) {
        if (Math.abs(data.Y[i] - temp) < 0.5) {
          points.push({ x: data.X[i], z: data.Z[i] })
        }
      }
      console.log(`  Temp ${temp.toFixed(1)}°C: found ${points.length} raw points`)
      
      // Deduplicate by X, keep best match
      const xMap = new Map()
      points.forEach(p => {
        const xKey = p.x.toFixed(6)
        if (!xMap.has(xKey)) {
          xMap.set(xKey, p)
        }
      })
      const sliceData = Array.from(xMap.values()).map(p => [p.x, p.z]).sort((a, b) => a[0] - b[0])
      return {
        temp,
        data: sliceData,
        color: TEMP_COLORS[idx % TEMP_COLORS.length],
        isReference: temp === 25 // Highlight 25°C as reference
      }
    })

    // Calculate axis limits from all temperature slices
    const allXValues = tempSlices.flatMap(s => s.data.map(d => d[0]))
    const allZValuesChart1 = tempSlices.flatMap(s => s.data.map(d => d[1]))
    const xMin = allXValues.length > 0 ? Math.min(...allXValues) : 0
    const xMax = allXValues.length > 0 ? Math.max(...allXValues) : 1
    const xRange = xMax - xMin
    
    // Calculate Z limits for Chart 1 (temperature curves)
    const zRangeChart1 = allZValuesChart1.length > 0 ? Math.max(...allZValuesChart1) - Math.min(...allZValuesChart1) : 1
    let zMinChart1 = allZValuesChart1.length > 0
      ? (allowNegativeZ ? Math.min(...allZValuesChart1) - zRangeChart1 * 0.1 : 0)
      : 0
    let zMaxChart1 = allZValuesChart1.length > 0 ? Math.max(...allZValuesChart1) + zRangeChart1 * 0.1 : 1

    // === Build 4 spec points for Chart 1 (at T_amb=25°C) ===
    const isPowerDomain = !data.xLabel || data.xLabel === 'Power'
    let specPointsChart1 = []

    // Get electrical values for X-axis (Power or Current)
    // Field names must match dataService.js normalizeProductData exports
    const P_C10_raw = cellData?.electrical?.P_dc_C10 || 0
    const P_dc_25_raw = cellData?.electrical?.P_dc_25_W || 0  // Note: _W suffix in dataService
    const P_cont_raw = cellData?.electrical?.P_dc_cont || 0
    const P_peak_raw = cellData?.electrical?.P_dc_peak || 0
    const I_C10_raw = cellData?.electrical?.I_dc_C10 || 0
    const I_dc_25_raw = cellData?.electrical?.I_dc_25_A || 0  // Note: _A suffix in dataService
    const I_cont_raw = cellData?.electrical?.I_dc_cont || 0
    const I_peak_raw = cellData?.electrical?.I_dc_peak || 0

    // DEBUG: Log raw electrical values for spec points
    console.log('[ThermalDimensionView] Electrical values for spec points:', {
      isPowerDomain,
      P_C10_raw, P_dc_25_raw, P_cont_raw, P_peak_raw,
      I_C10_raw, I_dc_25_raw, I_cont_raw, I_peak_raw
    })

    const X_C10 = scaleX(isPowerDomain ? P_C10_raw : I_C10_raw)
    const X_dc_25 = scaleX(isPowerDomain ? P_dc_25_raw : I_dc_25_raw)
    const X_cont = scaleX(isPowerDomain ? P_cont_raw : I_cont_raw)
    const X_peak = scaleX(isPowerDomain ? P_peak_raw : I_peak_raw)

    // Compute thermal spec point values from DCC arrays (handles case when dataService didn't have electrical data)
    const computed = computeThermalSpecPoints(cellData, categoryName)
    
    // DEBUG: Log thermal values from cellData and computed
    console.log('[ThermalDimensionView] Thermal values for spec points:', {
      categoryName,
      computed,
      fromDataService: { 
        P_loss: { c10: cellData?.thermal?.P_loss_c10, dc_25: cellData?.thermal?.P_loss_dc_25, cont: cellData?.thermal?.P_loss_cont, peak: cellData?.thermal?.P_loss_peak }
      }
    })

    if (categoryName === 'heat-power') {
      // P_loss values - prefer computed, fallback to dataService
      specPointsChart1 = [
        { x: X_C10, z: scaleZ(computed.c10 || cellData?.thermal?.P_loss_c10 || 0), name: 'C/10' },
        { x: X_dc_25, z: scaleZ(computed.dc_25 || cellData?.thermal?.P_loss_dc_25 || 0), name: 'Standard' },
        { x: X_cont, z: scaleZ(computed.cont || cellData?.thermal?.P_loss_cont || 0), name: 'Continuous' },
        { x: X_peak, z: scaleZ(computed.peak || cellData?.thermal?.P_loss_peak || 0), name: 'Peak' }
      ]
    } else if (categoryName === 'heat-energy') {
      // W_loss values [Wh] - prefer computed, fallback to dataService
      specPointsChart1 = [
        { x: X_C10, z: scaleZ(computed.c10 || cellData?.thermal?.W_loss_c10 || 0), name: 'C/10' },
        { x: X_dc_25, z: scaleZ(computed.dc_25 || cellData?.thermal?.W_loss_dc_25 || 0), name: 'Standard' },
        { x: X_cont, z: scaleZ(computed.cont || cellData?.thermal?.W_loss_cont || 0), name: 'Continuous' },
        { x: X_peak, z: scaleZ(computed.peak || cellData?.thermal?.W_loss_peak || 0), name: 'Peak' }
      ]
    } else if (categoryName === 'temperature') {
      // T_max values [°C] - prefer computed, fallback to dataService
      specPointsChart1 = [
        { x: X_C10, z: computed.c10 || cellData?.thermal?.T_max_c10 || 0, name: 'C/10' },
        { x: X_dc_25, z: computed.dc_25 || cellData?.thermal?.T_max_dc_25 || 0, name: 'Standard' },
        { x: X_cont, z: computed.cont || cellData?.thermal?.T_max_cont || 0, name: 'Continuous' },
        { x: X_peak, z: computed.peak || cellData?.thermal?.T_max_peak || 0, name: 'Peak' }
      ]
    } else if (categoryName === 'efficiency') {
      // eta values [%] - prefer computed, fallback to dataService
      specPointsChart1 = [
        { x: X_C10, z: computed.c10 || cellData?.thermal?.eta_c10 || 0, name: 'C/10' },
        { x: X_dc_25, z: computed.dc_25 || cellData?.thermal?.eta_dc_25 || 0, name: 'Standard' },
        { x: X_cont, z: computed.cont || cellData?.thermal?.eta_cont || 0, name: 'Continuous' },
        { x: X_peak, z: computed.peak || cellData?.thermal?.eta_peak || 0, name: 'Peak' }
      ]
    }

    console.log('Chart 1 spec points:', specPointsChart1)

    // === RENDER CHART 1: Z vs X with multi-temp curves and spec points ===
    if (chartInstance1.current) chartInstance1.current.dispose()
    chartInstance1.current = echarts.init(plot2dRef1.current)

    // Build series: temperature curve lines (Row 2) + spec point scatters (Row 1)
    // SAME STRUCTURE AS ELECTRICAL DimensionView.jsx
    const specPointNames = specPointsChart1.map(sp => sp.name)
    const tempNames = tempSlices.map(s => `${s.temp.toFixed(0)}°C`)
    
    const chart1Series = [
      // Temperature curves: one line per unique temperature (same as electrical)
      ...tempSlices.map(slice => ({
        type: 'line',
        name: `${slice.temp.toFixed(0)}°C`,
        data: slice.data,
        lineStyle: { 
          color: slice.color, 
          width: 3,
          type: 'solid'
        },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: slice.color },
        smooth: false,
        emphasis: {
          lineStyle: { width: 4 },
          itemStyle: { borderWidth: 2, borderColor: '#fff' }
        }
      })),
      // Spec point scatter series (4 points at T=25°C)
      ...specPointsChart1.map(sp => ({
        type: 'scatter',
        name: sp.name,
        data: [[sp.x, sp.z]],
        symbol: 'circle',
        symbolSize: 12,
        itemStyle: { color: specPointColors[sp.name], borderColor: '#fff', borderWidth: 2 },
        z: 10
      }))
    ]

    chartInstance1.current.setOption({
      backgroundColor: 'transparent',
      grid: { left: 70, right: 30, top: 50, bottom: 55 },
      legend: [
        // Row 1: Spec Points (scatter markers)
        {
          show: true,
          top: 0,
          left: 'center',
          orient: 'horizontal',
          data: specPointNames,
          textStyle: { color: '#cfd2d6', fontSize: 9, fontFamily: 'Roboto' },
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 12
        },
        // Row 2: Temperature curves
        {
          show: tempSlices.length > 1,
          top: 18,
          left: 'center',
          orient: 'horizontal',
          data: tempNames,
          textStyle: { color: '#cfd2d6', fontSize: 9, fontFamily: 'Roboto' },
          itemWidth: 18,
          itemHeight: 3,
          itemGap: 8
        }
      ],
      xAxis: {
        type: 'value',
        name: `${data.xLabel} / ${data.xUnit}`,
        nameLocation: 'middle',
        nameGap: 40,
        min: xMin - xRange * 0.05,
        max: xMax + xRange * 0.05,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: { show: true, color: '#999', fontFamily: 'Roboto', fontSize: 11, showMinLabel: false, showMaxLabel: false },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      yAxis: {
        type: 'value',
        name: `${data.zLabel} / ${data.zUnit}`,
        nameLocation: 'middle',
        nameGap: 50,
        min: zMinChart1,
        max: zMaxChart1,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: { show: true, color: '#999', fontFamily: 'Roboto', fontSize: 11, showMinLabel: false, showMaxLabel: false },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      series: chart1Series,
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(45, 42, 38, 0.95)',
        borderColor: '#ea5b0c',
        borderWidth: 1,
        textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 },
        axisPointer: {
          type: 'line',
          lineStyle: { color: 'rgba(255, 255, 255, 0.3)', type: 'dashed' }
        },
        formatter: (params) => {
          if (!params || params.length === 0) return ''
          
          // First line: X-axis value with unit
          let tooltipText = `<strong>${data.xLabel}: ${formatValue(params[0].value[0], data.xUnit)} ${data.xUnit}</strong><br/>`
          
          // Show all series values at this X position
          params.forEach((param) => {
            const value = param.value[1]
            const color = param.color
            tooltipText += `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:5px;"></span>${param.seriesName}: ${formatValue(value, data.zUnit)} ${data.zUnit}<br/>`
          })
          
          return tooltipText
        }
      }
    })

    // === CHART 2: Z vs Y (Temperature), one curve per spec point condition ===
    // Build Y-slices at each of the 4 X-values (P_C10, P_dc_25, P_cont, P_peak)
    // IMPORTANT: Use SCALED X values to match specPointsChart1
    const specPointXValues = [
      { name: 'C/10', x: X_C10, x_raw: isPowerDomain ? P_C10_raw : I_C10_raw },
      { name: 'Standard', x: X_dc_25, x_raw: isPowerDomain ? P_dc_25_raw : I_dc_25_raw },
      { name: 'Continuous', x: X_cont, x_raw: isPowerDomain ? P_cont_raw : I_cont_raw },
      { name: 'Peak', x: X_peak, x_raw: isPowerDomain ? P_peak_raw : I_peak_raw }
    ]

    console.log(`🔍 === SPEC POINT X VALUES (${categoryName}) ===`)
    specPointXValues.forEach(sp => {
      console.log(`  ${sp.name}: X_scaled=${sp.x.toFixed(2)}, X_raw=${sp.x_raw.toFixed(2)}, ratio=${(sp.x / sp.x_raw).toFixed(4)}`)
    })

    // For each spec point, find all data at that X value (with tolerance)
    const specPointSlices = specPointXValues.map((sp, idx) => {
      // Use SCALED X value for matching (same as specPointsChart1.x)
      const targetX = sp.x
      const tolerance = Math.abs(targetX) * 0.1 || 0.1 // 10% tolerance or 0.1 minimum
      const points = []
      
      for (let i = 0; i < data.X.length; i++) {
        // data.X is already in display basis (after scaleX applied in getDataForCategory)
        if (Math.abs(data.X[i] - targetX) <= tolerance) {
          points.push({ y: data.Y[i], z: data.Z[i], x: data.X[i], xDist: Math.abs(data.X[i] - targetX) })
        }
      }
      
      // Deduplicate by Y: keep the point closest to targetX (not the first one)
      const yMap = new Map()
      points.forEach(p => {
        const yKey = p.y.toFixed(3)
        if (!yMap.has(yKey)) {
          yMap.set(yKey, p)
        } else {
          // If we already have a point for this Y, keep the one closer to targetX
          const existing = yMap.get(yKey)
          if (p.xDist < existing.xDist) {
            yMap.set(yKey, p)
          }
        }
      })
      const sliceData = Array.from(yMap.values()).map(p => [p.y, p.z]).sort((a, b) => a[0] - b[0])
      return {
        name: sp.name,
        x: sp.x,
        data: sliceData,
        color: specPointColors[sp.name]
      }
    })

    // DEBUG: Detailed spec point slice analysis
    console.log(`🔍 === SPEC POINT SLICES DEBUG (${categoryName}) ===`)
    console.log(`specPointSlices:`, specPointSlices)
    specPointSlices.forEach((slice, idx) => {
      const specPoint = specPointsChart1[idx]
      console.log(`  ${slice.name}:`)
      if (!slice.x && slice.x !== 0) {
        console.warn(`    WARNING: slice.x is undefined for ${slice.name}`, slice)
        return
      }
      console.log(`    Spec Point X: ${specPoint.x.toFixed(2)} (scaled), Z: ${specPoint.z.toFixed(2)} (scaled)`)
      console.log(`    Slice search X: ${slice.x.toFixed(2)} ± ${Math.abs(slice.x) * 0.1 || 0.1}`)
      console.log(`    Found ${slice.data.length} points in slice`)
      if (slice.data.length > 0) {
        console.log(`    Slice data (Y, Z): ${slice.data.map(p => `(${p[0].toFixed(1)}°C, ${p[1].toFixed(2)})`).join(', ')}`)
        // Check if any point is close to spec point
        const closestPoint = slice.data.reduce((closest, p) => {
          const dist = Math.abs(p[1] - specPoint.z)
          return dist < Math.abs(closest[1] - specPoint.z) ? p : closest
        }, slice.data[0])
        const zDiff = Math.abs(closestPoint[1] - specPoint.z)
        console.log(`    Closest Z value: ${closestPoint[1].toFixed(2)}, Diff from spec Z: ${zDiff.toFixed(2)} (${(zDiff / Math.abs(specPoint.z) * 100).toFixed(1)}%)`)
        
        // DEBUG: Show all raw data points that were considered
        const tolerance = Math.abs(slice.x) * 0.1 || 0.1
        const rawPointsInRange = []
        for (let i = 0; i < data.X.length; i++) {
          if (Math.abs(data.X[i] - slice.x) <= tolerance) {
            rawPointsInRange.push({ i, X: data.X[i].toFixed(2), Y: data.Y[i].toFixed(1), Z: data.Z[i].toFixed(2) })
          }
        }
        console.log(`    Raw data points in X-range (${slice.x.toFixed(2)}±${tolerance.toFixed(2)}):`)
        rawPointsInRange.forEach(p => console.log(`      [${p.i}] X=${p.X}, Y=${p.Y}°C, Z=${p.Z}`))
      }
    })

    console.log('Chart 2 spec point slices:', specPointSlices.map(s => ({ name: s.name, points: s.data.length })))

    // Calculate axis limits for Chart 2
    const allYValues = specPointSlices.flatMap(s => s.data.map(d => d[0]))
    const allZValuesChart2 = specPointSlices.flatMap(s => s.data.map(d => d[1]))
    const yMin = allYValues.length > 0 ? Math.min(...allYValues) : 0
    const yMax = allYValues.length > 0 ? Math.max(...allYValues) : 1
    const yRange = yMax - yMin
    const zRangeChart2 = allZValuesChart2.length > 0 ? Math.max(...allZValuesChart2) - Math.min(...allZValuesChart2) : 1
    let zMinChart2 = allZValuesChart2.length > 0
      ? (allowNegativeZ ? Math.min(...allZValuesChart2) - zRangeChart2 * 0.1 : 0)
      : 0
    let zMaxChart2 = allZValuesChart2.length > 0 ? Math.max(...allZValuesChart2) + zRangeChart2 * 0.1 : 1
    
    // Use same scale for both charts (for consistent visual comparison)
    if (allowNegativeZ) {
      // For negative values (heat-power/heat-energy): sync both min and max
      const commonZMin = Math.min(zMinChart1, zMinChart2)
      const commonZMax = Math.max(zMaxChart1, zMaxChart2)
      zMinChart1 = commonZMin
      zMaxChart1 = commonZMax
      zMinChart2 = commonZMin
      zMaxChart2 = commonZMax
    } else {
      // For non-negative values: only sync max
      const commonZMax = Math.max(zMaxChart1, zMaxChart2)
      zMaxChart1 = commonZMax
      zMaxChart2 = commonZMax
    }

    // Build 4 spec point scatter series for Chart 2 (at T_amb=25°C)
    const specPointsChart2 = specPointsChart1.map(sp => ({
      name: sp.name,
      y: 25, // T_amb = 25°C
      z: sp.z,
      color: specPointColors[sp.name]
    }))

    // === RENDER CHART 2: Z vs Temperature with 4 condition curves ===
    if (chartInstance2.current) chartInstance2.current.dispose()
    chartInstance2.current = echarts.init(plot2dRef2.current)

    // Build series: condition curve lines + spec point scatters (same as electrical)
    const chart2Series = [
      // Condition curves (4 lines, same colors as spec points)
      ...specPointSlices.filter(slice => slice.data.length > 0).map(slice => ({
        type: 'line',
        name: slice.name,
        data: slice.data,
        lineStyle: { color: slice.color, width: 3 },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: slice.color },
        smooth: false,
        emphasis: {
          lineStyle: { width: 4 },
          itemStyle: { borderWidth: 2, borderColor: '#fff' }
        }
      })),
      // Spec point scatter series (4 points at T=25°C)
      ...specPointsChart2.map(sp => ({
        type: 'scatter',
        name: `${sp.name} (25°C)`,
        data: [[sp.y, sp.z]],
        symbol: 'circle',
        symbolSize: 12,
        itemStyle: { color: sp.color, borderColor: '#fff', borderWidth: 2 },
        z: 10
      }))
    ]

    // Build legend names for Chart 2 (same as electrical)
    const specPointNamesChart2 = specPointsChart2.map(sp => `${sp.name} (25°C)`)
    const curveNamesChart2 = specPointSlices.filter(slice => slice.data.length > 0).map(s => s.name)

    chartInstance2.current.setOption({
      backgroundColor: 'transparent',
      grid: { left: 70, right: 30, top: 50, bottom: 55 },
      legend: [
        // Row 1: Spec Points (scatter markers at 25°C)
        {
          show: specPointSlices.length > 1,
          top: 0,
          left: 'center',
          orient: 'horizontal',
          data: specPointNamesChart2,
          textStyle: { color: '#cfd2d6', fontSize: 9, fontFamily: 'Roboto' },
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 12
        },
        // Row 2: Condition curves (same colors as spec points)
        {
          show: specPointSlices.length > 1,
          top: 18,
          left: 'center',
          orient: 'horizontal',
          data: curveNamesChart2,
          textStyle: { color: '#cfd2d6', fontSize: 9, fontFamily: 'Roboto' },
          itemWidth: 18,
          itemHeight: 3,
          itemGap: 12
        }
      ],
      xAxis: {
        type: 'value',
        name: `${data.yLabel} / ${data.yUnit}`,
        nameLocation: 'middle',
        nameGap: 40,
        min: yMin - yRange * 0.05,
        max: yMax + yRange * 0.05,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: { show: true, color: '#999', fontFamily: 'Roboto', fontSize: 11, showMinLabel: false, showMaxLabel: false },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      yAxis: {
        type: 'value',
        name: `${data.zLabel} / ${data.zUnit}`,
        nameLocation: 'middle',
        nameGap: 50,
        min: zMinChart2,
        max: zMaxChart2,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: { show: true, color: '#999', fontFamily: 'Roboto', fontSize: 11, showMinLabel: false, showMaxLabel: false },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      series: chart2Series,
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(45, 42, 38, 0.95)',
        borderColor: '#0d7c99',
        borderWidth: 1,
        textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 },
        axisPointer: {
          type: 'line',
          lineStyle: { color: 'rgba(255, 255, 255, 0.3)', type: 'dashed' }
        },
        formatter: (params) => {
          if (!params || params.length === 0) return ''
          
          // First line: X-axis value (Ambient Temperature) with unit
          let tooltipText = `<strong>${data.yLabel}: ${formatValue(params[0].value[0], data.yUnit)} ${data.yUnit}</strong><br/>`
          
          // Show all series values at this temperature
          params.forEach((param) => {
            const value = param.value[1]
            const color = param.color
            tooltipText += `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:5px;"></span>${param.seriesName}: ${formatValue(value, data.zUnit)} ${data.zUnit}<br/>`
          })
          
          return tooltipText
        }
      }
    })
  }

  // ============================================================================================
  // MATLAB-STYLE DELAUNAY TRIANGULATION (like MATLAB's `delaunay(x,y)`)
  // ============================================================================================
  // Reference: MATLAB uses `T = delaunay(x,y)` to create triangulation from scattered points
  // Then `trisurf(T, x, y, z)` renders the surface using the triangulation
  // This is a 2D Delaunay triangulation implementation using the Bowyer-Watson algorithm
  // with improved numerical stability through point normalization
  function computeDelaunayTriangles(points) {
    if (points.length < 3) return []
    
    // Find bounding box
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
    points.forEach(p => {
      minX = Math.min(minX, p.x)
      maxX = Math.max(maxX, p.x)
      minY = Math.min(minY, p.y)
      maxY = Math.max(maxY, p.y)
    })
    
    const dx = maxX - minX || 1
    const dy = maxY - minY || 1
    
    // Normalize points to [0, 1] range for better numerical stability
    const normalizedPoints = points.map((p, idx) => ({
      x: (p.x - minX) / dx,
      y: (p.y - minY) / dy,
      idx: idx
    }))
    
    // Create MUCH larger super-triangle to ensure all points are well inside
    // This prevents edge triangles from being lost
    const superTriangle = {
      a: { x: -10, y: -10, idx: -3 },
      b: { x: 0.5, y: 20, idx: -2 },
      c: { x: 20, y: -10, idx: -1 }
    }
    
    let triangles = [superTriangle]
    
    // Helper: Check if point is inside triangle's circumcircle
    function inCircumcircle(p, tri) {
      const ax = tri.a.x, ay = tri.a.y
      const bx = tri.b.x, by = tri.b.y
      const cx = tri.c.x, cy = tri.c.y
      
      const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by))
      if (Math.abs(d) < 1e-14) return false
      
      const ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d
      const uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d
      const r2 = (ax - ux) * (ax - ux) + (ay - uy) * (ay - uy)
      
      const dist2 = (p.x - ux) * (p.x - ux) + (p.y - uy) * (p.y - uy)
      // Use small epsilon for numerical stability (point ON circle should be considered inside)
      return dist2 < r2 + 1e-10
    }
    
    // Insert each point using Bowyer-Watson algorithm
    normalizedPoints.forEach((p) => {
      const badTriangles = []
      const polygon = []
      
      // Find all triangles whose circumcircle contains the point
      triangles.forEach(tri => {
        if (inCircumcircle(p, tri)) {
          badTriangles.push(tri)
        }
      })
      
      // Find the boundary polygon of bad triangles
      badTriangles.forEach(tri => {
        const edges = [
          [tri.a, tri.b],
          [tri.b, tri.c],
          [tri.c, tri.a]
        ]
        
        edges.forEach(edge => {
          // Check if edge is shared with another bad triangle
          let isShared = false
          badTriangles.forEach(other => {
            if (other === tri) return
            const otherEdges = [
              [other.a, other.b],
              [other.b, other.c],
              [other.c, other.a]
            ]
            otherEdges.forEach(oe => {
              if ((edge[0].idx === oe[0].idx && edge[1].idx === oe[1].idx) ||
                  (edge[0].idx === oe[1].idx && edge[1].idx === oe[0].idx)) {
                isShared = true
              }
            })
          })
          
          if (!isShared) {
            polygon.push(edge)
          }
        })
      })
      
      // Remove bad triangles
      triangles = triangles.filter(t => !badTriangles.includes(t))
      
      // Create new triangles from the point to each edge in the polygon
      polygon.forEach(edge => {
        triangles.push({
          a: edge[0],
          b: edge[1],
          c: p
        })
      })
    })
    
    // Remove triangles that contain super-triangle vertices
    triangles = triangles.filter(tri => {
      return tri.a.idx >= 0 && tri.b.idx >= 0 && tri.c.idx >= 0
    })
    
    console.log(`Delaunay: ${points.length} points -> ${triangles.length} triangles`)
    
    // Convert to index arrays for Plotly mesh3d
    return triangles.map(tri => ({
      a: tri.a.idx,
      b: tri.b.idx,
      c: tri.c.idx
    }))
  }

  // ============================================================================================
  // MATLAB-STYLE SCATTERED INTERPOLANT (like MATLAB's `scatteredInterpolant(x',y',z')`)
  // ============================================================================================
  // Reference: MATLAB creates an interpolant `F = scatteredInterpolant(x',y',z')`
  // Then uses `F(x0, y0)` to evaluate Z at any arbitrary (x0, y0) position
  // This implementation uses inverse distance weighting (IDW) for smooth interpolation
  function createScatteredInterpolant(points, values) {
    // Points: array of {x, y}
    // Values: array of z values corresponding to each point
    
    return function interpolate(x, y) {
      // Find nearest points and use inverse distance weighting
      const distances = []
      
      for (let i = 0; i < points.length; i++) {
        const dx = points[i].x - x
        const dy = points[i].y - y
        const dist = Math.sqrt(dx * dx + dy * dy)
        
        // Exact match - return the value directly
        if (dist < 1e-10) {
          return values[i]
        }
        
        distances.push({ idx: i, dist, value: values[i] })
      }
      
      // Sort by distance and take nearest N points for IDW
      distances.sort((a, b) => a.dist - b.dist)
      const nearestN = Math.min(8, distances.length)  // Use up to 8 nearest neighbors
      
      let weightSum = 0
      let valueSum = 0
      
      for (let i = 0; i < nearestN; i++) {
        const weight = 1 / Math.pow(distances[i].dist, 2)  // Inverse square distance
        weightSum += weight
        valueSum += weight * distances[i].value
      }
      
      return valueSum / weightSum
    }
  }

  function render3DPlot(data, categoryName) {
    if (!plot3dRef.current) return

    console.log(`=== Rendering 3D ${categoryName} with Plotly (MATLAB-style Delaunay) ===`)

    // === MATLAB-STYLE: Use original scattered data points directly ===
    // Like MATLAB's `T = delaunay(x,y)` + `trisurf(T, x, y, z)`
    // Group data by temperature to create regular grid rows
    const tempGroups = new Map()
    for (let i = 0; i < data.X.length; i++) {
      const tempKey = Math.round(data.Y[i] * 10) / 10  // Round to 0.1°C precision
      if (!tempGroups.has(tempKey)) {
        tempGroups.set(tempKey, [])
      }
      tempGroups.get(tempKey).push({ power: data.X[i], value: data.Z[i] })
    }

    // Sort temperature keys
    const uniqueY = Array.from(tempGroups.keys()).sort((a, b) => a - b)
    
    // Get unique X values for slice generation
    const allX = new Set()
    for (let i = 0; i < data.X.length; i++) {
      allX.add(data.X[i])
    }
    const uniqueX = Array.from(allX).sort((a, b) => a - b)
    
    console.log(`  Raw data: ${data.X.length} points`)
    console.log(`  Unique X values: ${uniqueX.length}, range [${uniqueX[0]?.toFixed(2)}, ${uniqueX[uniqueX.length-1]?.toFixed(2)}]`)
    console.log(`  Unique Y values: ${uniqueY.length}, range [${uniqueY[0]?.toFixed(1)}°C, ${uniqueY[uniqueY.length-1]?.toFixed(1)}°C]`)

    // === CREATE SCATTERED POINTS FOR DELAUNAY (MATLAB's delaunay(x,y)) ===
    const scatteredPoints = []
    for (let i = 0; i < data.X.length; i++) {
      scatteredPoints.push({ x: data.X[i], y: data.Y[i], idx: i })
    }
    
    // === COMPUTE DELAUNAY TRIANGULATION ===
    const triangles = computeDelaunayTriangles(scatteredPoints)
    
    // Convert triangles to Plotly mesh3d format (i, j, k arrays)
    const meshI = []
    const meshJ = []
    const meshK = []
    triangles.forEach(tri => {
      meshI.push(tri.a)
      meshJ.push(tri.b)
      meshK.push(tri.c)
    })
    
    console.log('=== Delaunay Triangulation ===')
    console.log(`Points: ${scatteredPoints.length}, Triangles: ${triangles.length}`)

    // === CREATE WIREFRAME EDGES FROM TRIANGLES (like MATLAB's trisurf grid lines) ===
    // Extract unique edges from triangles to show the grid structure
    const edgeSet = new Set()
    const wireframeX = []
    const wireframeY = []
    const wireframeZ = []
    
    triangles.forEach(tri => {
      // Create edge keys (sorted to avoid duplicates)
      const edges = [
        [Math.min(tri.a, tri.b), Math.max(tri.a, tri.b)],
        [Math.min(tri.b, tri.c), Math.max(tri.b, tri.c)],
        [Math.min(tri.c, tri.a), Math.max(tri.c, tri.a)]
      ]
      
      edges.forEach(([i, j]) => {
        const key = `${i}-${j}`
        if (!edgeSet.has(key)) {
          edgeSet.add(key)
          // Add line segment with null separator for Plotly
          wireframeX.push(data.X[i], data.X[j], null)
          wireframeY.push(data.Y[i], data.Y[j], null)
          wireframeZ.push(data.Z[i], data.Z[j], null)
        }
      })
    })
    
    console.log(`Wireframe: ${edgeSet.size} unique edges`)

    // === CREATE SLICES USING ACTUAL DATA POINTS (not interpolated) ===
    
    // Slice at Y0 (T = 25°C): Find actual data points near T=25°C
    const y0 = data.Y0 || 25
    const createSliceAtY = (targetY, tolerance = 2) => {
      const sliceData = []
      for (let i = 0; i < data.Y.length; i++) {
        if (Math.abs(data.Y[i] - targetY) <= tolerance) {
          sliceData.push({ x: data.X[i], y: targetY, z: data.Z[i] })
        }
      }
      
      // Sort by X
      sliceData.sort((a, b) => a.x - b.x)
      
      // Remove duplicates - keep average Z for same X
      const xMap = new Map()
      sliceData.forEach(d => {
        const xKey = Math.round(d.x * 100) / 100
        if (xMap.has(xKey)) {
          const existing = xMap.get(xKey)
          xMap.set(xKey, { x: xKey, y: targetY, z: (existing.z + d.z) / 2 })
        } else {
          xMap.set(xKey, { x: xKey, y: targetY, z: d.z })
        }
      })
      
      return Array.from(xMap.values()).sort((a, b) => a.x - b.x)
    }
    
    const sliceAtY0 = createSliceAtY(y0)
    
    // Slice at X0: Find actual data points near X0 (Power Standard)
    const x0 = data.X0
    const createSliceAtX = (targetX, tolerance = 0.3) => {
      if (!targetX || targetX === 0) return []
      
      const sliceData = []
      for (let i = 0; i < data.X.length; i++) {
        const xDiff = Math.abs(data.X[i] - targetX) / Math.max(Math.abs(targetX), 0.001)
        if (xDiff <= tolerance) {
          sliceData.push({ x: targetX, y: data.Y[i], z: data.Z[i] })
        }
      }
      
      // Sort by Y (temperature)
      sliceData.sort((a, b) => a.y - b.y)
      
      // Remove duplicates - keep average Z for same Y
      const yMap = new Map()
      sliceData.forEach(d => {
        const yKey = Math.round(d.y * 10) / 10
        if (yMap.has(yKey)) {
          const existing = yMap.get(yKey)
          yMap.set(yKey, { x: targetX, y: yKey, z: (existing.z + d.z) / 2 })
        } else {
          yMap.set(yKey, { x: targetX, y: yKey, z: d.z })
        }
      })
      
      return Array.from(yMap.values()).sort((a, b) => a.y - b.y)
    }
    
    const sliceAtX0 = createSliceAtX(x0)
    
    console.log('=== Slices (using actual data points) ===')
    console.log(`Y0 slice (T=${y0}°C): ${sliceAtY0.length} points`)
    console.log(`X0 slice (P=${x0?.toFixed(2)}): ${sliceAtX0.length} points`)

    // === RENDER WITH PLOTLY (MATLAB-STYLE mesh3d) ===
    const traces = [
      // === MESH3D: Delaunay triangulated surface (like MATLAB's trisurf) ===
      {
        type: 'mesh3d',
        x: data.X,
        y: data.Y,
        z: data.Z,
        i: meshI,
        j: meshJ,
        k: meshK,
        intensity: data.Z,
        colorscale: categoryName === 'heat-dissipation' 
          ? [
              [0, 'rgb(13, 124, 153)'],      // Cyan (niedrige Werte)
              [0.3, 'rgb(45, 42, 38)'],      // Dunkelgrau
              [0.6, 'rgb(234, 91, 12)'],     // Orange (mittlere Werte)
              [1, 'rgb(255, 200, 100)']      // Hellorange (hohe Werte)
            ]
          : [
              [0, 'rgb(45, 42, 38)'],        // Dunkelgrau (niedrig)
              [0.5, 'rgb(234, 91, 12)'],     // Orange (mittel)
              [1, 'rgb(255, 200, 100)']      // Hellorange (hoch)
            ],
        opacity: 0.7,
        showscale: false,
        flatshading: false,  // Smooth shading like MATLAB
        lighting: {
          ambient: 0.6,
          diffuse: 0.8,
          specular: 0.3,
          roughness: 0.5,
          fresnel: 0.2
        },
        lightposition: {
          x: 5000,
          y: 5000,
          z: 5000
        },
        hovertemplate: `${data.xLabel}: %{x:.2f} ${data.xUnit}<br>` +
                       `${data.yLabel}: %{y:.1f} ${data.yUnit}<br>` +
                       `${data.zLabel}: %{z:.2f} ${data.zUnit}<extra></extra>`
      },
      // === WIREFRAME: Triangle edges (like MATLAB's trisurf grid lines) ===
      {
        type: 'scatter3d',
        mode: 'lines',
        name: 'Grid',
        x: wireframeX,
        y: wireframeY,
        z: wireframeZ,
        line: {
          color: 'rgba(234, 91, 12, 0.15)',  // Subtle orange wireframe
          width: 1
        },
        hoverinfo: 'skip',
        showlegend: false
      },
      // === SCATTER3D: Raw data points (like MATLAB's markers on trisurf) ===
      {
        type: 'scatter3d',
        mode: 'markers',
        name: 'Measured Points',
        x: data.X,
        y: data.Y,
        z: data.Z,
        marker: {
          size: 3,
          color: data.Z,
          colorscale: [
            [0, 'rgb(45, 42, 38)'],
            [0.5, 'rgb(234, 91, 12)'],
            [1, 'rgb(255, 200, 100)']
          ],
          opacity: 0.8
        },
        hovertemplate: `${data.xLabel}: %{x:.2f} ${data.xUnit}<br>${data.yLabel}: %{y:.1f} ${data.yUnit}<br>${data.zLabel}: %{z:.2f} ${data.zUnit}<extra></extra>`
      },
      // === SLICE at Y0 (T = 25°C) - like MATLAB's plot3(x_plot, y0*ones(...), zx_plot, 'r') ===
      ...(sliceAtY0.length >= 2 ? [{
        type: 'scatter3d',
        mode: 'lines',
        name: `T = ${y0}°C`,
        x: sliceAtY0.map(d => d.x),
        y: sliceAtY0.map(d => d.y),
        z: sliceAtY0.map(d => d.z),
        line: {
          color: '#ea5b0c',  // Orange
          width: 8
        },
        hovertemplate: `${data.xLabel}: %{x:.2f} ${data.xUnit}<br>${data.yLabel}: %{y:.1f} ${data.yUnit}<br>${data.zLabel}: %{z:.2f} ${data.zUnit}<extra></extra>`
      }] : []),
      // === SLICE at X0 - like MATLAB's plot3(x0*ones(...), y_plot, zy_plot, 'g') ===
      ...(sliceAtX0.length >= 2 ? [{
        type: 'scatter3d',
        mode: 'lines',
        name: `P = ${formatValue(x0, data.xUnit)} ${data.xUnit}`,
        x: sliceAtX0.map(d => d.x),
        y: sliceAtX0.map(d => d.y),
        z: sliceAtX0.map(d => d.z),
        line: {
          color: '#0d7c99',  // Cyan
          width: 8
        },
        hovertemplate: `${data.xLabel}: %{x:.2f} ${data.xUnit}<br>${data.yLabel}: %{y:.1f} ${data.yUnit}<br>${data.zLabel}: %{z:.2f} ${data.zUnit}<extra></extra>`
      }] : [])
    ]

    // Layout (matched to Electrical section)
    const layout = {
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      scene: {
        xaxis: {
          title: { text: `${data.xLabel} (${data.xUnit})`, font: { color: '#cfd2d6', family: 'Roboto' } },
          gridcolor: 'rgba(234, 91, 12, 0.15)',  // Orange grid
          zerolinecolor: 'rgba(255, 255, 255, 0.2)',
          tickfont: { color: '#999', family: 'Roboto' },
          backgroundcolor: 'rgba(0, 0, 0, 0)',
          automargin: true  // Verhindert Abschneiden
        },
        yaxis: {
          title: { text: `${data.yLabel} (${data.yUnit})`, font: { color: '#cfd2d6', family: 'Roboto' } },
          gridcolor: 'rgba(234, 91, 12, 0.15)',  // Orange grid
          zerolinecolor: 'rgba(255, 255, 255, 0.2)',
          tickfont: { color: '#999', family: 'Roboto' },
          backgroundcolor: 'rgba(0, 0, 0, 0)',
          automargin: true  // Verhindert Abschneiden
        },
        zaxis: {
          title: { text: `${data.zLabel} (${data.zUnit})`, font: { color: '#cfd2d6', family: 'Roboto' } },
          gridcolor: 'rgba(234, 91, 12, 0.15)',  // Orange grid
          zerolinecolor: 'rgba(255, 255, 255, 0.2)',
          tickfont: { color: '#999', family: 'Roboto' },
          backgroundcolor: 'rgba(0, 0, 0, 0)',
          automargin: true  // Verhindert Abschneiden
        },
        bgcolor: 'rgba(0, 0, 0, 0)',
        camera: {
          eye: { x: 1.5, y: -1.5, z: 1.3 }  // Rotated 90° CW from default
        }
      },
      margin: { l: 0, r: 0, t: 0, b: 0 },
      autosize: true,  // Automatische Größenanpassung
      showlegend: true,  // Legende aktiviert
      legend: {
        x: 0.98,  // Innerhalb des Plots (98%)
        y: 0.98,  // Oben (98%)
        xanchor: 'right',  // Rechte Ecke des Plot-Bereichs
        yanchor: 'top',
        font: { color: '#cfd2d6', family: 'Roboto', size: 10 },
        bgcolor: 'rgba(16, 16, 16, 0.85)',
        bordercolor: 'rgba(234, 91, 12, 0.3)',
        borderwidth: 1
      }
    }

    // Config (matched to Electrical section)
    const config = {
      responsive: true,
      displayModeBar: false,  // Toolbar ausblenden wie bei Electrical
      displaylogo: false
    }

    // Render
    window.Plotly.newPlot(plot3dRef.current, traces, layout, config)
  }

  // Render logic based on dimension
  if (dimension === '1d') {
    // Use data from state
    if (!data1D) {
      return (
      <div className="dimension-view-container">
          <div className="dimension-1d">
            <p style="color: #999;">No thermal data available</p>
          </div>
        </div>
      )
    }

    // Render 4 color-coded tiles matching Energy/Power pattern
    return (
      <div className="dimension-view-container">
        <div className="dimension-1d">
          <div className="kpi-grid-4">
            {/* C/10 Tile */}
            <div className="kpi-tile" style={`border-color: ${data1D.c10.color}`}>
              <div className="kpi-value" style={`color: ${data1D.c10.color}`}>
                {formatValue(data1D.c10.value, data1D.c10.unit)}
              </div>
              <div className="kpi-unit">{data1D.c10.unit}</div>
              <div className="kpi-label">{data1D.c10.label}</div>
            </div>

            {/* Standard Tile */}
            <div className="kpi-tile" style={`border-color: ${data1D.dc_25.color}`}>
              <div className="kpi-value" style={`color: ${data1D.dc_25.color}`}>
                {formatValue(data1D.dc_25.value, data1D.dc_25.unit)}
              </div>
              <div className="kpi-unit">{data1D.dc_25.unit}</div>
              <div className="kpi-label">{data1D.dc_25.label}</div>
            </div>

            {/* Continuous Tile */}
            <div className="kpi-tile" style={`border-color: ${data1D.cont.color}`}>
              <div className="kpi-value" style={`color: ${data1D.cont.color}`}>
                {formatValue(data1D.cont.value, data1D.cont.unit)}
              </div>
              <div className="kpi-unit">{data1D.cont.unit}</div>
              <div className="kpi-label">{data1D.cont.label}</div>
            </div>

            {/* Peak Tile */}
            <div className="kpi-tile" style={`border-color: ${data1D.peak.color}`}>
              <div className="kpi-value" style={`color: ${data1D.peak.color}`}>
                {formatValue(data1D.peak.value, data1D.peak.unit)}
              </div>
              <div className="kpi-unit">{data1D.peak.unit}</div>
              <div className="kpi-label">{data1D.peak.label}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (dimension === '2d') {
    return (
      <div className="dimension-view-container">
        <div className="dimension-2d">
          <div className="plot-grid-2d">
            <div className="plot-card">
              <div ref={plot2dRef1} style="width: 100%; height: 100%;"></div>
            </div>
            <div className="plot-card">
              <div ref={plot2dRef2} style="width: 100%; height: 100%;"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (dimension === '3d') {
    return (
      <div className="dimension-view-container">
        <div className="dimension-3d">
          <div className="plot-3d-container">
            <div ref={plot3dRef} style="width: 100%; height: 100%;"></div>
          </div>
        </div>
      </div>
    )
  }

  return null
}
